import { useState, useEffect } from "react";
import { Apple } from "lucide-react";
import { Button } from "@/app/components/button";
import { Input } from "@/app/components/input";
import { SMLogo } from "@/app/components/sm-logo";
import { GoogleSSOModal } from "@/app/components/google-sso-modal";
import { AppleSSOModal, type AppleSignInResult } from "@/app/components/apple-sso-modal";
import { getAuthErrorMessage, getAuthErrorFields } from "@/app/utils/auth-error-message";
import { forgotPassword as apiForgotPassword } from "@/app/api/auth-client";

export interface GoogleAccount {
  id: string;
  name: string;
  email: string;
  picture: string;
}

export interface AppleAccount {
  id: string;
  name: string;
  email: string;
  picture: string;
  isPrivateEmail: boolean;
}

export interface SignUpResult {
  verifyEmail?: boolean;
}

interface LoginPageProps {
  /** Email/password login (calls Bonamassa). */
  onLogin: (email: string, password: string) => Promise<void>;
  /** Email/password signup (calls Bonamassa). Returns verifyEmail when user must verify email. */
  onSignUp?: (email: string, password: string, name: string, termsAccepted: boolean, privacyAccepted: boolean) => Promise<SignUpResult | void>;
  /** Google SSO: receives Google ID token, calls Bonamassa POST /auth/google, then sets user state. */
  onGoogleSSO?: (idToken: string) => Promise<void>;
  /** Apple SSO: receives Apple identity token and optional email/name, calls Bonamassa POST /auth/apple, then sets user state. */
  onAppleSSO?: (result: AppleSignInResult) => Promise<void>;
  /** Web OAuth Client ID for Google Sign-In (same as Bonamassa GOOGLE_OAUTH_CLIENT_ID). */
  googleClientId?: string;
  /** Apple Services ID for Sign in with Apple (same as Bonamassa APPLE_CLIENT_ID). */
  appleClientId?: string;
  /** Apple Return URL — must match exactly what is set in Apple Developer (Return URLs). */
  appleRedirectUri?: string;
  /** Called after signup success (verify email) countdown to redirect user to Home. */
  onSignupSuccessRedirect?: () => void;
  /** Called when user should go to Home after forgot-password success (avoids full reload so they see app Home, not login). */
  onForgotPasswordSuccessGoHome?: () => void;
  /** Called when user chooses to browse without logging in (go to home feed). */
  onBrowseWithoutLogin?: () => void;
  /** Tap logo → home (same as browsing without login when unset). */
  onLogoClick?: () => void;
  /** Open signup tab first (e.g. from header "Cadastrar"). */
  defaultSignUp?: boolean;
}

const SIGNUP_REDIRECT_SECONDS = 5;
const FORGOT_PASSWORD_REDIRECT_SECONDS = 5;

/**
 * Contas só OAuth não recebem e-mail de reset (Bonamassa).
 * O endpoint público não revela se o e-mail existe nem se tem senha (anti-enumeração), por isso não dá para
 * checar o e-mail no front nem bloquear o pedido sem um novo contrato na API.
 */
function ForgotPasswordSsoDisclosure({ initiallyExpanded = false }: { initiallyExpanded?: boolean }) {
  const className =
    "rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 text-left text-sm text-amber-900 dark:text-amber-100/95 [&_summary]:list-none [&_summary::-webkit-details-marker]:hidden";
  const body = (
    <>
      <summary className="cursor-pointer select-none px-3 py-2.5 font-medium text-amber-800 dark:text-amber-200 hover:text-amber-900 dark:hover:text-amber-100">
        Entrou só com Google ou Apple e não tem senha?
      </summary>
      <div className="border-t border-amber-200/80 dark:border-amber-800/80 px-3 pb-3 pt-2 text-amber-800/95 dark:text-amber-200/90 leading-snug">
        Nesse caso <strong>não enviamos e-mail</strong> de redefinição. Volte ao login e use{" "}
        <strong>Continuar com Google</strong> ou <strong>Continuar com Apple</strong>.
      </div>
    </>
  );
  return initiallyExpanded ? (
    <details open className={className}>
      {body}
    </details>
  ) : (
    <details className={className}>{body}</details>
  );
}

function safeText(x: unknown): string {
  if (x == null) return "";
  if (typeof x === "string") return x;
  if (typeof x === "number") return String(x);
  return "";
}

export function LoginPage({
  onLogin,
  onSignUp,
  onGoogleSSO,
  onAppleSSO,
  onSignupSuccessRedirect,
  onForgotPasswordSuccessGoHome,
  onBrowseWithoutLogin,
  onLogoClick,
  googleClientId,
  appleClientId,
  appleRedirectUri,
  defaultSignUp = false,
}: LoginPageProps) {
  const logoGoHome = onLogoClick ?? onBrowseWithoutLogin;
  const [isSignUp, setIsSignUp] = useState(defaultSignUp);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [passwordReadonly, setPasswordReadonly] = useState(true);
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false);
  const [isAppleModalOpen, setIsAppleModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordCountdown, setForgotPasswordCountdown] = useState<number | null>(null);

  useEffect(() => {
    setIsSignUp(defaultSignUp);
  }, [defaultSignUp]);

  // 5s countdown after signup success, then redirect to Home
  useEffect(() => {
    if (successMessage == null || countdownSeconds == null || !onSignupSuccessRedirect) return;
    if (countdownSeconds <= 0) {
      onSignupSuccessRedirect();
      return;
    }
    const t = setTimeout(() => setCountdownSeconds((s) => s! - 1), 1000);
    return () => clearTimeout(t);
  }, [successMessage, countdownSeconds, onSignupSuccessRedirect]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setSuccessMessage(null);
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    setLoading(true);
    try {
      if (isSignUp && onSignUp) {
        const result = await onSignUp(trimmedEmail, password, trimmedName, termsAccepted, privacyAccepted);
        if (result?.verifyEmail) {
          setSuccessMessage("Verifique seu e-mail para ativar sua conta. Depois faça login.");
          setCountdownSeconds(onSignupSuccessRedirect ? SIGNUP_REDIRECT_SECONDS : null);
        }
      } else {
        await onLogin(trimmedEmail, password);
      }
    } catch (err) {
      setError(getAuthErrorMessage(err));
      const fields = getAuthErrorFields(err);
      if (fields) setFieldErrors(fields);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleClick = () => {
    setIsGoogleModalOpen(true);
  };

  const handleGoogleAccountSelect = async (idToken: string) => {
    if (!onGoogleSSO) {
      setError("Login com Google em breve. Use e-mail e senha.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await onGoogleSSO(idToken);
      setIsGoogleModalOpen(false);
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleClick = () => {
    setIsAppleModalOpen(true);
  };

  const handleAppleAccountSelect = async (result: AppleSignInResult) => {
    if (!onAppleSSO) {
      setError("Login com Apple em breve. Use e-mail e senha.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await onAppleSSO(result);
      setIsAppleModalOpen(false);
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordFocus = () => {
    setPasswordReadonly(false);
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Digite seu e-mail.");
      return;
    }
    setLoading(true);
    try {
      await apiForgotPassword(trimmedEmail);
      setForgotPasswordSuccess(true);
      setForgotPasswordCountdown(FORGOT_PASSWORD_REDIRECT_SECONDS);
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const goHome = () => {
    if (onForgotPasswordSuccessGoHome) {
      onForgotPasswordSuccessGoHome();
    } else {
      window.location.href = "/";
    }
  };

  // 5s countdown after forgot-password success, then redirect to Home
  useEffect(() => {
    if (!forgotPasswordSuccess || forgotPasswordCountdown === null) return;
    if (forgotPasswordCountdown <= 0) {
      goHome();
      return;
    }
    const t = setTimeout(() => setForgotPasswordCountdown((c) => c! - 1), 1000);
    return () => clearTimeout(t);
  }, [forgotPasswordSuccess, forgotPasswordCountdown]);

  // Forgot-password view: single email input + success message
  if (isForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              {logoGoHome ? (
                <button
                  type="button"
                  onClick={logoGoHome}
                  className="rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary p-0 hover:opacity-90 transition-opacity"
                  aria-label="Ir para início"
                >
                  <SMLogo className="w-20 h-20 drop-shadow-lg" />
                </button>
              ) : (
                <SMLogo className="w-20 h-20 drop-shadow-lg" />
              )}
            </div>
            <h1 className="text-3xl mb-2">Esqueceu a senha?</h1>
            <p className="text-muted-foreground">
              Digite seu e-mail e enviaremos um link para redefinir sua senha.
            </p>
          </div>
          <div className="bg-card rounded-2xl shadow-xl p-8 border border-border space-y-4">
            {forgotPasswordSuccess ? (
              <>
                <p className="text-center text-muted-foreground">
                  Se esse e-mail estiver cadastrado e a conta tiver senha, você receberá um link para redefinir.
                </p>
                <ForgotPasswordSsoDisclosure initiallyExpanded />
                <div className="space-y-2">
                  <div className="h-2 w-full rounded-full bg-primary/20 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-1000 ease-linear"
                      style={{
                        width: `${forgotPasswordCountdown !== null ? ((FORGOT_PASSWORD_REDIRECT_SECONDS - forgotPasswordCountdown) / FORGOT_PASSWORD_REDIRECT_SECONDS) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <p className="text-center text-sm text-muted-foreground">
                    Redirecionando para o início em {forgotPasswordCountdown} {forgotPasswordCountdown === 1 ? "segundo" : "segundos"}…
                  </p>
                </div>
                <button
                  type="button"
                  onClick={goHome}
                  className="w-full text-primary hover:underline text-sm"
                >
                  Ir para o início agora
                </button>
              </>
            ) : (
              <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                <ForgotPasswordSsoDisclosure />
                {error && (
                  <div className="rounded-lg bg-destructive/10 text-destructive text-sm p-3">
                    {safeText(error)}
                  </div>
                )}
                <Input
                  type="email"
                  placeholder="E-mail"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  disabled={loading}
                />
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Enviando…" : "Enviar link"}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(false);
                    setError(null);
                  }}
                  className="w-full text-primary hover:underline text-sm"
                >
                  Voltar ao login
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {logoGoHome ? (
              <button
                type="button"
                onClick={logoGoHome}
                className="rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary p-0 hover:opacity-90 transition-opacity"
                aria-label="Ir para início"
              >
                <SMLogo className="w-20 h-20 drop-shadow-lg" />
              </button>
            ) : (
              <SMLogo className="w-20 h-20 drop-shadow-lg" />
            )}
          </div>
          <h1 className="text-3xl mb-2">Bem-vindo ao akihub</h1>
          <p className="text-muted-foreground">
            {isSignUp ? "Crie sua conta para começar" : "Entre para continuar"}
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-card rounded-2xl shadow-xl p-8 border border-border">
          {/* Social Login Buttons */}
          <div className="space-y-3 mb-6">
            <button
              onClick={handleGoogleClick}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white border-2 border-border rounded-lg hover:bg-accent transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Continuar com Google</span>
            </button>

            <button
              onClick={handleAppleClick}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-black text-white rounded-lg hover:bg-black/90 transition-colors"
            >
              <Apple className="w-5 h-5" />
              <span>Continuar com Apple</span>
            </button>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-card text-muted-foreground">Ou continue com email</span>
            </div>
          </div>

          {/* Error / success messages */}
          {error && (
            <div className="rounded-lg bg-destructive/10 text-destructive text-sm p-3">
              {safeText(error)}
            </div>
          )}
          {successMessage && (
            <div className="rounded-lg bg-green-500/10 text-green-700 dark:text-green-400 text-sm p-3 space-y-3">
              <p>{safeText(successMessage)}</p>
              {countdownSeconds != null && onSignupSuccessRedirect && (
                <div className="space-y-2">
                  <div className="h-2 w-full rounded-full bg-green-500/20 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-green-600 dark:bg-green-500 transition-all duration-1000 ease-linear"
                      style={{ width: `${((SIGNUP_REDIRECT_SECONDS - countdownSeconds) / SIGNUP_REDIRECT_SECONDS) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-green-700/80 dark:text-green-400/80">
                    Redirecionando para o início em {countdownSeconds} {countdownSeconds === 1 ? "segundo" : "segundos"}…
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Email Login Form */}
          <form
            onSubmit={handleEmailLogin}
            className="space-y-4"
            autoComplete={isSignUp ? "off" : "on"}
            data-form-type={isSignUp ? "other" : "login"}
          >
            {isSignUp && (
              <div>
                <label htmlFor="name" className="block text-sm mb-2">
                  Nome Completo
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder=""
                  value={name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setName(e.target.value);
                    if (fieldErrors.name) setFieldErrors((prev: Record<string, string>) => { const next = { ...prev }; delete next.name; return next; });
                  }}
                  autoComplete="name"
                  required
                  aria-invalid={!!fieldErrors.name}
                  aria-describedby={fieldErrors.name ? "name-error" : undefined}
                />
                {fieldErrors.name && (
                  <p id="name-error" className="text-sm text-destructive mt-1" role="alert">
                    {safeText(fieldErrors.name)}
                  </p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm mb-2">
                Endereço de Email
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder=""
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target.value);
                  if (fieldErrors.email) setFieldErrors((prev: Record<string, string>) => { const next = { ...prev }; delete next.email; return next; });
                }}
                autoComplete={isSignUp ? "email" : "username"}
                data-lpignore={isSignUp ? "true" : "false"}
                required
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? "email-error" : undefined}
              />
              {fieldErrors.email && (
                <p id="email-error" className="text-sm text-destructive mt-1" role="alert">
                  {safeText(fieldErrors.email)}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm mb-2">
                Senha
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder=""
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPassword(e.target.value);
                  if (fieldErrors.password) setFieldErrors((prev: Record<string, string>) => { const next = { ...prev }; delete next.password; return next; });
                }}
                autoComplete={isSignUp ? "new-password" : "current-password"}
                data-lpignore={isSignUp ? "true" : "false"}
                required
                readOnly={isSignUp ? passwordReadonly : false}
                onFocus={handlePasswordFocus}
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? "password-error" : undefined}
              />
              {fieldErrors.password && (
                <p id="password-error" className="text-sm text-destructive mt-1" role="alert">
                  {safeText(fieldErrors.password)}
                </p>
              )}
            </div>

            {isSignUp && (
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 rounded border-border"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    aria-describedby="terms-description"
                  />
                  <span id="terms-description" className="text-muted-foreground">
                    Li e aceito os{" "}
                    <button type="button" className="text-primary hover:underline">Termos de Serviço</button>.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 rounded border-border"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    aria-describedby="privacy-description"
                  />
                  <span id="privacy-description" className="text-muted-foreground">
                    Li e aceito a{" "}
                    <button type="button" className="text-primary hover:underline">Política de Privacidade</button>.
                  </span>
                </label>
              </div>
            )}

            {!isSignUp && (
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 rounded border-border" />
                  <span className="text-muted-foreground">Lembrar-me</span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(true)}
                  className="text-primary hover:underline"
                >
                  Esqueceu a senha?
                </button>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading || (isSignUp && (!termsAccepted || !privacyAccepted))}
            >
              {loading ? (isSignUp ? "Cadastrando…" : "Entrando…") : isSignUp ? "Criar Conta" : "Entrar"}
            </Button>
          </form>

          {/* Toggle Sign Up / Sign In */}
          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">
              {isSignUp ? "Já tem uma conta?" : "Não tem uma conta?"}
            </span>{" "}
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setPasswordReadonly(true);
                if (!isSignUp) {
                  setTermsAccepted(false);
                  setPrivacyAccepted(false);
                }
              }}
              className="text-primary hover:underline"
            >
              {isSignUp ? "Entrar" : "Cadastrar"}
            </button>
          </div>
        </div>

        {/* Browse without login */}
        {onBrowseWithoutLogin && (
          <p className="text-center mt-4">
            <button
              type="button"
              onClick={onBrowseWithoutLogin}
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
            >
              Continuar sem login e ver anúncios
            </button>
          </p>
        )}

        {/* Terms and Privacy */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Ao continuar, você concorda com nossos{" "}
          <button className="text-primary hover:underline">Termos de Serviço</button>
          {" "}e{" "}
          <button className="text-primary hover:underline">Política de Privacidade</button>
        </p>
      </div>
      <GoogleSSOModal
        isOpen={isGoogleModalOpen}
        onClose={() => setIsGoogleModalOpen(false)}
        onSelectAccount={handleGoogleAccountSelect}
        googleClientId={googleClientId ?? ""}
      />
      <AppleSSOModal
        isOpen={isAppleModalOpen}
        onClose={() => setIsAppleModalOpen(false)}
        onSelectAccount={handleAppleAccountSelect}
        appleClientId={appleClientId ?? ""}
        appleRedirectUri={appleRedirectUri}
      />
    </div>
  );
}